/**
 * Avisar só quem mudou: a escala publicada continua publicada nas edições pontuais, e cada
 * pessoa cuja situação mudou ganha um registro em `event.notices` — saiu, mudou de função
 * ou voltou —, que vira o aviso dela (`notifications`). Quem ficou como estava não recebe
 * nada. Na escala que ainda não foi publicada não há o que avisar: ninguém a viu.
 */
import type { ScheduleEvent, ScheduleNotice, ScheduleNoticeKind } from './types';

/** A função em que a pessoa está no evento (a primeira, se estiver em mais de uma). */
function roleOf(event: ScheduleEvent, memberId: string): string | null {
  return Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0] ?? null;
}

function everyone(event: ScheduleEvent): string[] {
  return Object.values(event.slots).flat();
}

/**
 * Compara a escala antes e depois de uma edição e anota em `notices` o que cada pessoa
 * precisa saber. Só vale quando a escala já estava publicada. Quem saiu porque avisou que
 * não pode ir não é avisado (foi ela que pediu). Voltar à mesma função de antes desfaz o
 * aviso da saída — quem nunca soube que saiu não precisa saber que voltou.
 */
export function trackChanges(before: ScheduleEvent, after: ScheduleEvent): ScheduleEvent {
  if (!before.published) return after;

  const notices: Record<string, ScheduleNotice> = { ...after.notices };
  let changed = false;
  const set = (memberId: string, notice: ScheduleNotice | null) => {
    const current = notices[memberId];

    if (!notice) {
      if (current) {
        delete notices[memberId];
        changed = true;
      }

      return;
    }
    if (current?.kind === notice.kind && current.roleId === notice.roleId) return;
    notices[memberId] = notice;
    changed = true;
  };

  for (const memberId of new Set([...everyone(before), ...everyone(after)])) {
    const was = roleOf(before, memberId);
    const now = roleOf(after, memberId);
    const previous = before.notices[memberId];

    if (was === now) continue;

    if (was && !now) {
      if (after.responses[memberId]?.status !== 'declined') set(memberId, { kind: 'removed', roleId: was });
    } else if (was && now) {
      set(memberId, { kind: 'moved', roleId: now });
    } else if (now && previous?.kind === 'removed') {
      // Voltou: à mesma função, o aviso da saída some; a outra, avisa que voltou.
      set(memberId, previous.roleId === now ? null : { kind: 'added', roleId: now });
    }
  }

  return changed ? { ...after, notices } : after;
}

/** O que vira e-mail numa edição da escala publicada: os avisos novos e quem entrou nela. */
export type ScheduleChangeKind = ScheduleNoticeKind | 'assigned';

export interface ScheduleChange {
  memberId: string;
  kind: ScheduleChangeKind;
  /** A função de que saiu (`removed`) ou em que está agora. */
  roleId: string;
}

/**
 * Quem precisa ser avisado por uma edição da escala que já estava (e continua) publicada:
 * cada aviso novo ou diferente em `notices` e quem entrou sem aviso (`assigned` — a vaga
 * preenchida, a pessoa que voltou à mesma função). Quem ficou como estava não aparece.
 */
export function changesToSend(before: ScheduleEvent, after: ScheduleEvent): ScheduleChange[] {
  if (!before.published || !after.published) return [];

  const ids = new Set([...everyone(before), ...everyone(after), ...Object.keys(after.notices)]);

  return [...ids].flatMap((memberId): ScheduleChange[] => {
    const notice = after.notices[memberId];
    const previous = before.notices[memberId];

    if (notice && (notice.kind !== previous?.kind || notice.roleId !== previous.roleId)) {
      return [{ memberId, ...notice }];
    }

    const now = roleOf(after, memberId);

    return !notice && now && !roleOf(before, memberId) ? [{ memberId, kind: 'assigned', roleId: now }] : [];
  });
}
